<!--
  SAX Component Palette - Kit-Based Component Browser

  FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/SaxComponentPalette.vue

  Displays components from equipment's selected kits
  Kit-based navigation with Manage Kits button
-->

<template>
  <div class="sax-component-palette">
    <!-- Header -->
    <div class="palette-header">
      <div class="header-title">
        <div class="text-subtitle2 text-grey-9 q-pa-sm">
          <q-icon name="widgets" class="q-mr-xs" />
          Component Library
        </div>

        <!-- Manage Kits Button -->
        <q-btn
          flat
          dense
          icon="settings"
          label="Manage Kits"
          color="primary"
          size="sm"
          class="q-ma-sm"
          @click="openKitManager"
        />
      </div>

      <!-- Kit Selection Dropdown -->
      <div v-if="availableKits.length > 0" class="kit-selector q-px-sm q-pb-sm">
        <q-select
          v-model="selectedKitName"
          :options="kitOptions"
          label="Select a Kit"
          dense
          outlined
          options-dense
          emit-value
          map-options
          class="kit-dropdown"
        >
          <template #prepend>
            <q-icon name="category" />
          </template>
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>{{ scope.opt.label }}</q-item-label>
                <q-item-label caption>
                  v{{ scope.opt.version }}
                  <q-badge v-if="scope.opt.isBasic" color="green" class="q-ml-xs">Basic</q-badge>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
      </div>

      <!-- No kits warning -->
      <div v-else class="q-pa-md text-center">
        <q-icon name="warning" size="md" color="warning" />
        <div class="text-caption text-grey-7 q-mt-sm">
          No kits selected for this equipment
        </div>
        <q-btn
          outline
          color="primary"
          label="Manage Kits"
          size="sm"
          icon="settings"
          class="q-mt-sm"
          @click="openKitManager"
        />
      </div>
    </div>

    <!-- Component List -->
    <div class="palette-scroll-area">
      <div v-if="isLoading" class="q-pa-md text-center">
        <q-spinner color="primary" size="3em" />
        <div class="text-caption text-grey-7 q-mt-sm">Loading components...</div>
      </div>

      <div v-else-if="!selectedKitName" class="q-pa-md text-center">
        <q-icon name="category" size="3em" color="grey-5" />
        <div class="text-caption text-grey-7 q-mt-sm">
          Please select a kit from the dropdown above
        </div>
      </div>

      <div v-else-if="componentTypes.length === 0 && availableKits.length > 0" class="q-pa-md text-center">
        <q-icon name="inbox" size="3em" color="grey-5" />
        <div class="text-caption text-grey-7 q-mt-sm">
          No components found in selected kits
        </div>
      </div>

      <div v-else-if="filteredComponents.length === 0 && selectedKitName" class="q-pa-md text-center">
        <q-icon name="search_off" size="3em" color="grey-5" />
        <div class="text-caption text-grey-7 q-mt-sm">
          No components in {{ selectedKitName }}
        </div>
      </div>

      <div v-else class="component-list q-pa-sm">
        <!-- Group by kit (when showing all) or category (when kit selected) -->
        <div
          v-for="(group, groupKey) in groupedComponents"
          :key="groupKey"
          class="component-category q-mb-md"
        >
          <div class="category-header text-caption text-grey-7 q-px-xs q-py-xs">
            {{ groupKey || 'Uncategorized' }}
          </div>

          <div class="category-items">
            <div
              v-for="component in group"
              :key="component.id"
              class="component-item"
              draggable="true"
              @dragstart="handleDragStart($event, component)"
              @dragend="handleDragEnd"
              @click="selectComponent(component)"
            >
              <q-card
                flat
                bordered
                class="component-card"
                :class="{ 'component-card-selected': selectedComponentId === component.id }"
              >
                <q-card-section class="q-pa-sm">
                  <!-- Component Icon/Preview -->
                  <div class="component-preview">
                    <div
                      class="component-preview-box"
                      :style="{
                        width: `${Math.min(component.width / 3, 45)}px`,
                        height: `${Math.min(component.height / 3, 30)}px`,
                        backgroundColor: component.color || '#3B82F6',
                      }"
                    >
                      <!-- Input slots (excluding config slots) -->
                      <div
                        v-for="(slot, index) in getRuntimeSlots(component.input_slots)"
                        :key="`in-${index}`"
                        class="slot-indicator slot-input"
                        :style="{
                          top: `${getSlotPosition(component, index, getRuntimeSlots(component.input_slots).length)}px`,
                          backgroundColor: getSlotColor(slot.type)
                        }"
                      ></div>

                      <!-- Output slots (excluding config slots) -->
                      <div
                        v-for="(slot, index) in getRuntimeSlots(component.output_slots)"
                        :key="`out-${index}`"
                        class="slot-indicator slot-output"
                        :style="{
                          top: `${getSlotPosition(component, index, getRuntimeSlots(component.output_slots).length)}px`,
                          backgroundColor: getSlotColor(slot.type)
                        }"
                      ></div>
                    </div>
                  </div>

                  <!-- Component Name -->
                  <div class="component-name text-caption text-weight-medium text-grey-9 q-mt-xs">
                    {{ component.name }}
                  </div>

                  <!-- Component Kit (if showing all kits) -->
                  <div v-if="!selectedKitName" class="component-type text-caption text-grey-6">
                    {{ component.kit_name }}
                  </div>

                  <!-- Slot Counts -->
                  <div class="component-slots text-caption text-grey-7 q-mt-xs">
                    <q-icon name="input" size="xs" color="green" />
                    {{ getRuntimeSlots(component.input_slots).length }}

                    <q-icon name="output" size="xs" color="orange" class="q-ml-xs" />
                    {{ getRuntimeSlots(component.output_slots).length }}
                  </div>
                </q-card-section>

                <!-- Hover Actions -->
                <q-card-actions class="component-actions q-pa-xs">
                  <q-btn
                    icon="info"
                    size="xs"
                    flat
                    dense
                    @click.stop="showComponentInfo(component)"
                  >
                    <q-tooltip>View details</q-tooltip>
                  </q-btn>

                  <q-space />

                  <q-btn
                    v-if="!readonly"
                    icon="add"
                    size="xs"
                    flat
                    dense
                    color="primary"
                    @click.stop="addComponentToCanvas(component)"
                  >
                    <q-tooltip>Add to canvas</q-tooltip>
                  </q-btn>
                </q-card-actions>
              </q-card>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="palette-footer">
      <div class="text-caption text-grey-7 q-px-sm q-pb-xs">
        {{ componentTypes.length }} components
        <span v-if="selectedKitName"> in {{ selectedKitName }}</span>
        <span v-else-if="availableKits.length > 0"> from {{ availableKits.length }} kits</span>
      </div>
    </div>

    <!-- Component Info Dialog -->
    <q-dialog v-model="showInfoDialog">
      <q-card style="min-width: 400px">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">{{ selectedComponentForInfo?.name }}</div>
          <q-space />
          <q-btn v-close-popup icon="close" flat round dense />
        </q-card-section>

        <q-card-section v-if="selectedComponentForInfo">
          <div class="q-gutter-sm">
            <!-- Kit -->
            <div>
              <div class="text-caption text-grey-7">Kit</div>
              <div class="text-body2">{{ selectedComponentForInfo.kit_name }}</div>
            </div>

            <!-- Category -->
            <div v-if="selectedComponentForInfo.category">
              <div class="text-caption text-grey-7">Category</div>
              <div class="text-body2">{{ selectedComponentForInfo.category }}</div>
            </div>

            <!-- Dimensions -->
            <div>
              <div class="text-caption text-grey-7">Dimensions</div>
              <div class="text-body2">
                {{ selectedComponentForInfo.width }} × {{ selectedComponentForInfo.height }}
              </div>
            </div>

            <!-- Input Slots -->
            <div v-if="selectedComponentForInfo.input_slots?.length > 0">
              <div class="text-caption text-grey-7">Input Slots</div>
              <q-list dense bordered separator class="rounded-borders">
                <q-item
                  v-for="(slot, index) in selectedComponentForInfo.input_slots"
                  :key="index"
                  dense
                >
                  <q-item-section avatar>
                    <q-icon name="input" color="green" size="xs" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ slot.name }}</q-item-label>
                    <q-item-label caption>{{ slot.type || 'any' }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>

            <!-- Output Slots -->
            <div v-if="selectedComponentForInfo.output_slots?.length > 0">
              <div class="text-caption text-grey-7">Output Slots</div>
              <q-list dense bordered separator class="rounded-borders">
                <q-item
                  v-for="(slot, index) in selectedComponentForInfo.output_slots"
                  :key="index"
                  dense
                >
                  <q-item-section avatar>
                    <q-icon name="output" color="orange" size="xs" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ slot.name }}</q-item-label>
                    <q-item-label caption>{{ slot.type || 'any' }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>

            <!-- Default Properties -->
            <div v-if="selectedComponentForInfo.default_properties">
              <div class="text-caption text-grey-7">Default Properties</div>
              <q-list dense bordered separator class="rounded-borders">
                <q-item
                  v-for="(value, key) in selectedComponentForInfo.default_properties"
                  :key="key"
                  dense
                >
                  <q-item-section>
                    <q-item-label>{{ key }}</q-item-label>
                    <q-item-label caption>{{ value }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            v-if="!readonly"
            v-close-popup
            label="Add to Canvas"
            color="primary"
            flat
            @click="addComponentToCanvas(selectedComponentForInfo)"
          />
          <q-btn v-close-popup label="Close" flat />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Kit Manager Dialog -->
    <EquipmentKitManager
      v-model="showKitManager"
      :equipment-id="equipmentId"
      :equipment-name="equipmentName"
      @kits-updated="handleKitsUpdated"
    />
  </div>
</template>

<script setup>

  import { ref, computed, onMounted, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { useSaxEditorStore } from '@/stores/saxEditor';
  import { api } from '@/services/api';
  import EquipmentKitManager from './EquipmentKitManager.vue';


  // Props
  const props = defineProps({
    equipmentId: {
      type: Number,
      required: true,
    },
    equipmentName: {
      type: String,
      default: 'Equipment',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  });

  // Emits
  const emit = defineEmits(['component-selected']);

  // Quasar
  const $q = useQuasar();

  // Store
  const saxStore = useSaxEditorStore();

  // State
  const componentTypes = ref([]);
  const availableKits = ref([]);
  const isLoading = ref(false);
  const selectedKitName = ref(null);
  const selectedComponentId = ref(null);
  const showInfoDialog = ref(false);
  const selectedComponentForInfo = ref(null);
  const showKitManager = ref(false);

  // Computed

  /**
   * Kit options for dropdown
   */
  const kitOptions = computed(() => {
    return availableKits.value.map((kit) => ({
      label: kit.kit_name,
      value: kit.kit_name,
      version: kit.kit_version,
      isBasic: kit.is_basic_kit,
    }));
  });

  /**
   * Components in the selected kit
   * Only shows components when a kit is selected
   */
  const filteredComponents = computed(() => {
    // No kit selected = no components shown (selector)
    if (!selectedKitName.value) {
      return [];
    }

    return componentTypes.value.filter((comp) => comp.kit_name === selectedKitName.value);
  });

  /**
   * Components grouped by category (since we always show one kit at a time)
   */
  const groupedComponents = computed(() => {
    const grouped = {};

    const components = filteredComponents.value;
    // Always group by category since we only show one kit at a time
    const groupBy = 'category';

    components.forEach((comp) => {
      const key = comp[groupBy] || 'Uncategorized';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(comp);
    });

    // Sort groups
    return Object.keys(grouped)
      .sort()
      .reduce((acc, key) => {
        acc[key] = grouped[key];
        return acc;
      }, {});
  });

  // Watch for equipment ID changes
  watch(() => props.equipmentId, () => {
    if (props.equipmentId) {
      loadComponentTypes();
    }
  });


  // Methods

  /**
   * Load component types from API (for equipment's kits)
   */
  async function loadComponentTypes() {
    isLoading.value = true;
    try {

      const response = await api.get(`/v2/sax/component-types?equipment_id=${props.equipmentId}`);

      // API service unwraps {success: true, data: {components: [...], meta: {...}}}
      // So response = {components: [...], meta: {...}}
      if (response.components && Array.isArray(response.components)) {
        componentTypes.value = response.components;

        // Extract available kits from meta
        if (response.meta?.active_kits) {
          availableKits.value = response.meta.active_kits;
        }
      } else {
        // Fallback for empty or unexpected response
        componentTypes.value = [];
        availableKits.value = [];
      }

      // If no kits, show kit manager hint
      if (availableKits.value.length === 0) {
        $q.notify({
          type: 'info',
          message: 'No kits selected for this equipment',
          caption: 'Click "Manage Kits" to add kits',
          timeout: 3000,
        });
      }
    } catch (error) {
      console.error('[SAX Palette] Error loading component types:', error);
      $q.notify({
        type: 'negative',
        message: 'Failed to load component types',
        caption: error.message || 'Unknown error',
      });
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Get runtime slots (exclude config slots with flags: "c")
   */
  function getRuntimeSlots(slots) {
    if (!slots) {return [];}
    return slots.filter((slot) => !slot.flags || !slot.flags.includes('c'));
  }

  /**
   * Calculate slot position based on component height and slot count
   */
  function getSlotPosition(component, slotIndex, totalSlots) {
    // Get the scaled preview box height
    const boxHeight = Math.min(component.height / 3, 30);

    // Calculate spacing between slots
    const slotSpacing = totalSlots > 1 ? boxHeight / (totalSlots + 1) : boxHeight / 2;

    // Position slots evenly distributed within the box height
    return slotSpacing * (slotIndex + 1);
  }

  /**
   * Get slot color based on data type
   */
  function getSlotColor(slotType) {
    const typeColors = {
      'float': '#4CAF50',   // Green for float (analog values)
      'int': '#2196F3',     // Blue for int (digital values)
      'bool': '#FF9800',    // Orange for bool (binary states)
    };

    return typeColors[slotType] || '#9E9E9E'; // Gray for 'other' or undefined
  }

  /**
   * Open kit manager dialog
   */
  function openKitManager() {
    showKitManager.value = true;
  }

  /**
   * Handle kits updated from kit manager
   */
  async function handleKitsUpdated() {
    await loadComponentTypes();
  }

  /**
   * Handle drag start
   */
  function handleDragStart(event, component) {
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('application/json', JSON.stringify({
      type: 'component-type',
      data: component,
    }));

    // Create drag image
    const dragImage = event.target.cloneNode(true);
    dragImage.style.opacity = '0.5';
    event.dataTransfer.setDragImage(dragImage, 0, 0);
  }

  /**
   * Handle drag end
   */
  function handleDragEnd(event) {
    event.target.style.opacity = '1';
  }

  /**
   * Select component
   */
  function selectComponent(component) {
    selectedComponentId.value = component.id;
    emit('component-selected', component);
  }

  /**
   * Show component info dialog
   */
  function showComponentInfo(component) {
    selectedComponentForInfo.value = component;
    showInfoDialog.value = true;
  }

  /**
   * Add component to canvas at center
   */
  function addComponentToCanvas(component) {
    if (props.readonly) {return;}

    // Create a new component instance from the type
    const newComponent = {
      id: `comp_${Date.now()}`,
      name: component.name,
      type: component.name,
      kit_name: component.kit_name,
      position: {
        x: 100, // Default position, canvas will place it better
        y: 100,
      },
      width: component.width,
      height: component.height,
      color: component.color || '#3B82F6',
      properties: { ...(component.default_properties || {}) },
      input_slots: component.input_slots || [],
      output_slots: component.output_slots || [],
    };

    saxStore.addComponent(newComponent);

    $q.notify({
      type: 'positive',
      message: `Added ${component.name} to canvas`,
      timeout: 1000,
    });
  }

  // Lifecycle

  onMounted(() => {
    if (props.equipmentId) {
      loadComponentTypes();
    }
  });
</script>

<style scoped lang="scss">
.sax-component-palette {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: white;
}

.palette-header {
  border-bottom: 1px solid #e0e0e0;

  .header-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .kit-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-height: 120px;
    overflow-y: auto;
  }
}

.palette-scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  max-height: calc(100vh - 200px);
}

.component-list {
  .component-category {
    .category-header {
      text-transform: uppercase;
      font-weight: 600;
      letter-spacing: 0.5px;
      background: #f5f5f5;
      border-radius: 6px; // Slightly more rounded
      margin-bottom: 8px;
      padding: 8px 12px; // Better spacing
    }

    .category-items {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); // Smaller column width
      gap: 6px; // Tighter gap
    }
  }
}

.component-item {
  cursor: grab;
  transition: transform 0.2s;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    transform: translateY(-2px);
  }
}

.component-card {
  transition: all 0.2s;
  min-height: 80px; // Reduced from 120px
  border-radius: 6px; // Slightly smaller radius
  border: 1px solid #e0e0e0;

  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
    border-color: #2196F3;
    transform: translateY(-1px); // Subtle lift on hover
  }

  &-selected {
    border-color: #1976d2;
    background: #e3f2fd;
  }
}

.component-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 35px; // Reduced from 50px
  padding: 4px 0; // Minimal padding
}

.component-preview-box {
  position: relative;
  border-radius: 4px; // Smaller for compact look
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); // Lighter shadow
}

.slot-indicator {
  position: absolute;
  width: 5px; // Slightly smaller
  height: 5px;
  border-radius: 50%;
  border: 1px solid #FFFFFF; // White border for visibility
}

.slot-input {
  left: -2.5px; // Half in, half out
  // Background color applied via inline style based on data type
}

.slot-output {
  right: -2.5px; // Half in, half out
  // Background color applied via inline style based on data type
}

.component-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-size: 11px; // Slightly smaller
  font-weight: 500;
}

.component-type {
  text-align: center;
  font-size: 9px; // Smaller
  margin-top: 2px;
}

.component-slots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  font-size: 10px; // Smaller slot counts
  margin-top: 4px;
}

.component-actions {
  opacity: 0;
  transition: opacity 0.2s;
}

.component-card:hover .component-actions {
  opacity: 1;
}

.palette-footer {
  border-top: 1px solid #e0e0e0;
  padding: 8px;
  text-align: center;
}

// Responsive
@media (max-width: 768px) {
  .component-list {
    .component-category {
      .category-items {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      }
    }
  }
}
</style>
